import { computed, ref } from "vue";
import { storeToRefs } from "pinia";

import { useCategoriesStore } from "@/stores/Categories";
import type { Category } from "@/types/entities";

/*********************************************************************/
//                       NON-REACTIVE CONSTANTS                      //
/*********************************************************************/
import { consoleLoggerFactory, getDefaultLoggerOptions, type ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

/*********************************************************************/
//                 GLOBALLY SHARED REACTIVE VARIABLES                //
/*********************************************************************/

// A list of category ids for content that is associated with the categories in the collection (inclusive)
const includeCategoryFilter = ref< Category[] >( [] );

export function useCategoryFilter() {
    const categoriesStore = useCategoriesStore();
    const { allCategories } = storeToRefs( categoriesStore );

    const hasAnyCategory = computed< boolean >( (): boolean => allCategories.value.length > 0 );

    const categoryIdFilter = computed< string[] >( () => includeCategoryFilter.value.map( ( filter: Category ) => filter.id ) );

    // NOTE: Keep in mind that this has an "initialization side effect" since it mutates the globally shared list of selected categories
    function prePopulateSelectedCategories( preselectedSlugs: string[] ): void {
        resetSelectedCategories();
        preselectedSlugs.forEach( ( slug: string ): void => {
            const preselectedItem: Category | undefined = allCategories.value.find( ( item: Category ): boolean => item.slug === slug );
            if ( preselectedItem ) {
                includeCategoryFilter.value.push( preselectedItem );
            }
        } );
    }

    // NOTE: Keep in mind that this has an "initialization side effect" since it mutates the globally shared list of selected categories
    function resetSelectedCategories(): void {
        includeCategoryFilter.value = [];
    }

    function toggleSelectedCategory( item: Category ): void {
        if ( includeCategoryFilter.value.includes( item ) ) {
            const indexOfCategoryInSelectedList: number = includeCategoryFilter.value.indexOf( item );
            // remove the item from the list of currently selected categories
            includeCategoryFilter.value.splice( indexOfCategoryInSelectedList, 1 );
        } else {
            // add the item to the end of the list of selected categories
            includeCategoryFilter.value.push( item );
        }
    }

    function filterOutItemsThatDoNotContainSelectedCategories( categories: Category[] ): boolean {
        if ( categoryIdFilter.value?.length === 0 ) {
            return true;
        }

        logger.debug( '[ category-filter ] categoryIdFilter : ', categoryIdFilter.value );
        return categories.some( ( category: Category ) => categoryIdFilter.value.includes( category.id ) );
    }

    return {
        // ACTIONS
        filterOutItemsThatDoNotContainSelectedCategories,
        prePopulateSelectedCategories,
        resetSelectedCategories,
        toggleSelectedCategory,

        // GETTERS
        hasAnyCategory,
        includeCategoryFilter
    };
}

export type CategoryFilter = ReturnType< typeof useCategoryFilter >;
